import type { Optional } from '@/generic/functional';
import type { Description, ExampleCompliance, Severity, Title } from '@/rule';

type DraftExampleBuilder = {
  description: Optional<string>;
  snippet: Optional<string>;
  compliance: Optional<ExampleCompliance>;
};

export class DraftExample {
  readonly #description: Optional<string>;
  readonly #snippet: Optional<string>;
  readonly #compliance: Optional<ExampleCompliance>;

  private constructor(builder: DraftExampleBuilder) {
    this.#description = builder.description;
    this.#snippet = builder.snippet;
    this.#compliance = builder.compliance;
  }

  static of(builder: DraftExampleBuilder): DraftExample {
    return new DraftExample(builder);
  }

  get description(): Optional<string> {
    return this.#description;
  }

  get snippet(): Optional<string> {
    return this.#snippet;
  }

  get compliance(): Optional<ExampleCompliance> {
    return this.#compliance;
  }

  get isDocumented(): boolean {
    return this.#description.isPresent() || this.#snippet.isPresent();
  }

  get isPublishable(): boolean {
    return (
      this.#description.isPresent() && this.#snippet.isPresent() && this.#compliance.isPresent()
    );
  }
}

export class DraftRuleId {
  readonly #value: string;

  private constructor(value: string) {
    this.#value = value;
  }

  static of(value: DraftRuleId | string): DraftRuleId {
    if (value instanceof DraftRuleId) {
      return value;
    }
    return new DraftRuleId(value);
  }

  get value(): string {
    return this.#value;
  }
}

type DraftRuleBuilder = {
  id: DraftRuleId | string;
  title: Optional<Title>;
  description: Optional<Description>;
  severity: Optional<Severity>;
  examples: DraftExample[];
};

export class DraftRule {
  readonly #id: DraftRuleId;
  readonly #title: Optional<Title>;
  readonly #description: Optional<Description>;
  readonly #severity: Optional<Severity>;
  readonly #examples: DraftExample[];

  private constructor(builder: DraftRuleBuilder) {
    this.#id = DraftRuleId.of(builder.id);
    this.#title = builder.title;
    this.#description = builder.description;
    this.#severity = builder.severity;
    this.#examples = builder.examples;
  }

  static of(builder: DraftRuleBuilder): DraftRule {
    return new DraftRule(builder);
  }

  get id(): DraftRuleId {
    return this.#id;
  }

  get title(): Optional<Title> {
    return this.#title;
  }

  get description(): Optional<Description> {
    return this.#description;
  }

  get severity(): Optional<Severity> {
    return this.#severity;
  }

  get examples(): DraftExample[] {
    return this.#examples;
  }

  get documentedExamples(): DraftExample[] {
    return this.#examples.filter((example) => example.isDocumented);
  }

  get isPublishable(): boolean {
    return (
      this.#title.isPresent() &&
      this.#description.isPresent() &&
      this.#severity.isPresent() &&
      this.#examples.length > 0 &&
      this.#examples.every((example) => example.isPublishable)
    );
  }
}

export type DraftRuleEdit = {
  title: Optional<string>;
  description: Optional<string>;
  severity: Optional<Severity>;
  examples: DraftExample[];
};
